import type { Idea, QuoteBalance } from './api';

// ---------------------------------------------------------------------------
// The Quick Studio's shape, away from the components.
//
// Six tabs, a closed set. They are internal state on one view, not view ids,
// so the Pro sidebar, the director's Bench union and the tests that pin both
// are untouched by them. Each tab names the Pro bench that owns the same
// work and the control there that does it, so "Open in Pro" lands on the
// right bench with the right button lit rather than on a screen the creator
// then has to search.
//
// The price arithmetic is here and pure for the reason the timeline's is: a
// button that shows a number always renders, and a wrong number looks the
// same as a right one. A test can read this; it cannot read a button.
// ---------------------------------------------------------------------------

export const QUICK_TABS = ['image', 'edit', 'music', 'video', 'movie', 'library'] as const;
export type QuickTab = (typeof QUICK_TABS)[number];

export const TAB_LABEL: Record<QuickTab, string> = {
  image: 'Image', edit: 'Edit', music: 'Music', video: 'Video', movie: 'Movie', library: 'Library',
};

/** Where each tab's work lives in Pro, and which control there makes it. Null: the bench itself is the move. */
export const PRO_HANDOFF: Record<QuickTab, { bench: string; move: string | null }> = {
  image:   { bench: 'assets', move: 'assets-make' },
  edit:    { bench: 'assets', move: 'assets-make' },
  music:   { bench: 'audio',  move: 'audio-music' },
  video:   { bench: 'video',  move: 'scene-quote' },
  movie:   { bench: 'deck',   move: 'preview-play' },
  library: { bench: 'assets', move: null },
};

/** What "Open in Pro" carries across: the bench, the control, and the work so far. */
export interface Handoff {
  bench: string;
  move: string | null;
  /** The form as it stands, in the Idea Lab's shape, so the Pro bench reads it once the way it reads a proposal. */
  prefill?: Idea | null;
  /** The Movie tab's card, so the deck opens on it. */
  cardId?: string | null;
}
export type Hand = (h: Handoff) => void;

/** Credits for `units` at a rate, rounded up: a fraction of a credit is a credit. */
export function quoteFor(rate: { credits: number }, units: number): number {
  const n = Number.isFinite(units) && units > 0 ? units : 0;
  return Math.ceil(rate.credits * n);
}

/**
 * The price, in one sentence, with the balance after it when the server
 * sent one (204). Never blank: a quote line with nothing in it reads as
 * "free". An unmetered workspace hears only the price.
 */
export function sayQuote(credits: number, what: string, balance?: QuoteBalance | null): string {
  const price = credits > 0 ? `${credits.toLocaleString()} credits for ${what}` : `Nothing charged for ${what}`;
  if (!balance || balance.unmetered) return `${price}.`;
  const total = Math.max(0, Math.floor(balance.total));
  if (balance.ok) {
    return balance.purchased > 0
      ? `${price} · ${total.toLocaleString()} left (${Math.max(0, Math.floor(balance.plan)).toLocaleString()} from the plan, ${Math.floor(balance.purchased).toLocaleString()} bought).`
      : `${price} · ${total.toLocaleString()} left this month.`;
  }
  return `${price} · ${total.toLocaleString()} left, short by ${balance.shortBy.toLocaleString()} — this press would be refused.`;
}
